import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { ContentWrapper } from '@/components/dtcPartnerships/shared/styles';
import { useAnalytics } from '@/hooks/analytics';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';

import { SignupScreenProps } from '../shared/types';
import messages from './messages';
import { ErrorHeader, ErrorSupport, PaddedTop } from './styles';

const Error = ({ error, supportUrl, partnerDetails }: SignupScreenProps) => {
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	useComponentDidMount(() => {
		logEvent({
			eventName: 'Error : Landed',
			eventProps: {
				error: error?.code ?? null,
			},
		});
	});

	function getSupportLink(): string {
		return supportUrl ?? 'https://support.calm.com/hc/en-us';
	}

	function getErrorMessage(): string | ReactNode | null {
		if (!error?.code) return null;
		switch (error?.code) {
			case 'b2b_partner_not_found':
				return formatMessage(messages.partnerNotFound);
			case 'b2b_user_has_existing_subscription':
				return formatMessage(messages.userHasSubscription, {
					managesubscriptionlink: (...chunks: ReactNode[]) => (
						<a href="/profile/manage-subscription" target="_blank" rel="noopener noreferrer">
							{chunks}
						</a>
					),
				});
			case 'invalid_token':
				return formatMessage(messages.invalidToken, {
					supportlink: (...chunks: ReactNode[]) => (
						<a href={getSupportLink()} target="_blank" rel="noopener noreferrer">
							{chunks}
						</a>
					),
					partnerName: partnerDetails?.name,
				});
			case 'b2b_partner_eligibility_redeem_invalid_id':
				return formatMessage(messages.invalidOfferCodeId);
			case 'b2b_partner_subscription_upsert_user_conflict':
				return formatMessage(messages.alreadyRedeemed, {
					supportlink: (...chunks: ReactNode[]) => (
						<a href={getSupportLink()} target="_blank" rel="noopener noreferrer">
							{chunks}
						</a>
					),
					partnerName: partnerDetails?.name,
				});
			case 'user_cannot_trial':
				return formatMessage(messages.userCannotTrial, {
					supportlink: (...chunks: ReactNode[]) => (
						<a href="http://www.calm.com/contact" target="_blank" rel="noopener noreferrer">
							{chunks}
						</a>
					),
					partnerName: partnerDetails?.name,
				});
			case 'dtc_outside_eligible_region':
				return formatMessage(messages.userOutsideRegion, {
					supportlink: (...chunks: ReactNode[]) => (
						<a href="http://www.calm.com/contact" target="_blank" rel="noopener noreferrer">
							{chunks}
						</a>
					),
					partnerName: partnerDetails?.name,
				});
			default:
				return formatMessage(messages.errorCode, {
					error: (
						<PaddedTop>
							<b>{error.code}</b>
						</PaddedTop>
					),
				});
		}
	}

	const handledErrorMessage = getErrorMessage();

	return (
		<ContentWrapper>
			<ErrorHeader>{formatMessage(messages.sorry)}</ErrorHeader>
			{handledErrorMessage && <ErrorSupport>{handledErrorMessage}</ErrorSupport>}
			{error && error.partner_user_id && (
				<PaddedTop>
					{formatMessage(messages.participantId, {
						pid: <b>{error.partner_user_id}</b>,
					})}
				</PaddedTop>
			)}
		</ContentWrapper>
	);
};

export default Error;
